import classNames from 'classnames';
import { ReactNode, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { CloseButton } from 'atoms/buttons/CloseButton';
import { Vertical } from 'atoms/layout/flex';
import { Video } from 'atoms/ui/Video';
import { tabletAndAbove } from 'style/mediaQuery';

import { useResultsStoryAssets } from '../useResultsStoryAssets';
import { usePlayVideoManuallyOnError } from './usePlayVideoManuallyOnError';

const titleEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(50%);
  }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;

const buttonsEnter = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const contentsExit = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

export const Title = styled.div`
  font: var(--t-32);
  text-align: center;
  animation: ${titleEnter} 1s ease-out forwards;
  @media ${tabletAndAbove} {
    font: var(--t-48);
  }
`;
const VideoPlayer = styled(Video)`
  position: absolute;
  inset: 0;
`;
const BgVideo = styled(VideoPlayer)`
  opacity: 0;
  &.display {
    opacity: 1;
  }
`;
const Buttons = styled(Vertical)`
  position: absolute;
  min-width: 200px;
  bottom: var(--quadruple-unit);
  opacity: 0;
  animation: ${buttonsEnter} 1s ease-out 1.7s forwards;
`;
const Contents = styled(Vertical).attrs({ gap: 1, center: true })`
  isolation: isolate;

  &.isUnmounting {
    animation: ${contentsExit} 0.5s ease-out forwards;
  }
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;

type Props = {
  headlinePill?: ReactNode;
  title?: ReactNode;
  buttons?: ReactNode;
  isUnmounting: boolean;
  stopBg?: boolean;
  children?: ReactNode;
  setIsUnmounting: (isUnmounting: boolean) => void;
  onClose: () => void;
};
export const AnimatedIntro = ({
  headlinePill,
  title,
  buttons,
  isUnmounting,
  stopBg,
  children,
  setIsUnmounting,
  onClose,
}: Props) => {
  const { toIntro, introToEnd, introLoop } = useResultsStoryAssets();
  const [displayBgVideo, setDisplayBgVideo] = useState(false);
  const actionAfterOutro = (action?: () => void) => {
    setIsUnmounting(true);
    if (!action) return;
    setTimeout(action, 1000);
  };
  const introVideoRef = useRef<HTMLVideoElement>(null);
  const bgVideoRef = useRef<HTMLVideoElement>(null);
  const bgVideo = isUnmounting ? introToEnd : introLoop;
  const { onClick, onVideoError } = usePlayVideoManuallyOnError(introVideoRef);

  return (
    <>
      <VideoPlayer
        ref={introVideoRef}
        hideMutedIcon
        autoPlay
        muted
        playsInline
        onClick={onClick}
        onError={onVideoError}
        onEnded={() => {
          setDisplayBgVideo(true);
          bgVideoRef.current?.play();
        }}
        sources={toIntro}
      />
      <BgVideo
        key={bgVideo[0]?.src}
        ref={bgVideoRef}
        hideMutedIcon
        muted
        autoPlay={isUnmounting}
        loop={!isUnmounting && !stopBg}
        playsInline
        className={classNames({
          display: displayBgVideo,
        })}
        sources={bgVideo}
      />
      <Contents
        className={classNames({
          isUnmounting,
        })}
      >
        <CloseButtonWrapper>
          <CloseButton onClose={() => actionAfterOutro(onClose)} />
        </CloseButtonWrapper>
        {headlinePill}
        <Title>{title}</Title>
        {children}
        <Buttons gap={3} center>
          {buttons}
        </Buttons>
      </Contents>
    </>
  );
};
